/**
 * Enquiries from the public listings.
 *
 * **This screen is the reason the listing site is worth having.** Somebody found a flat, filled in
 * a form and is now waiting for a phone call; every hour that passes is an hour they spend looking
 * at somebody else's listing. So the list is ordered newest first, the message is on the row
 * rather than behind a click, and the two things worth doing — ring them, mark it answered — are
 * on the row too.
 *
 * The enquiries were being written long before anything could read them. The table existed, the
 * public form wrote to it, and no screen showed it. That is the gap this fills.
 */

import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { telHref, whatsappHref, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { Badge, Card, EmptyState, ErrorState, PageHeader, Select, Spinner, Table, Tabs } from "@/components/ui";

interface Row {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  message: string | null;
  source: string;
  status: string;
  createdAt: string;
  unitId: string | null;
  unitLabel: string | null;
  propertyName: string | null;
}

const TONE: Record<string, "danger" | "warn" | "info" | "muted"> = {
  new: "danger",
  contacted: "warn",
  viewing: "info",
  closed: "muted",
};

export function EnquiriesPage() {
  const [params] = useSearchParams();
  const status = params.get("status") ?? "";
  const { can, vocab } = useSession();
  const fmt = useFormat();
  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["enquiries", status, list.q, list.page],
    queryFn: () =>
      api.get<{ enquiries: Row[]; statuses: Array<{ value: string; label: string }>; meta: PageMeta }>("/enquiries", {
        status,
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const move = useApiMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => api.patch(`/enquiries/${id}`, { status: next }),
    invalidates: [["enquiries"]],
  });

  const rows = data?.enquiries ?? [];
  const statuses = data?.statuses ?? [];

  const tabs = [
    { to: "/app/enquiries", label: "Everything" },
    ...statuses.map((entry) => ({ to: `/app/enquiries?status=${entry.value}`, label: entry.label })),
  ];
  const current = status ? `/app/enquiries?status=${status}` : "/app/enquiries";

  return (
    <div>
      <PageHeader
        title="Listing enquiries"
        subtitle={`${data?.meta.total ?? 0} from your public listings`}
        description={`Somebody who found one of your ${vocab.units} on the listing site and asked about it. Ring them back the same day — they are looking at other agencies' listings too.`}
        actions={<ExportButton kind="enquiries" />}
      />

      <Tabs tabs={tabs} current={current} />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder="Search a name, phone or email…" />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={list.q ? `Nobody matched “${list.q}”.` : "No enquiries yet."}
            hint={`They arrive here when somebody asks about a ${vocab.unit} on your public listings. Switch a vacant ${vocab.unit} on to list it.`}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Who</th>
                <th>About</th>
                <th>Message</th>
                <th>Received</th>
                <th>Status</th>
                <th />
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="block font-medium text-ink-900">{row.name}</span>
                  <span className="block text-xs text-ink-500">
                    {row.phone ? (
                      <a href={telHref(row.phone)} className="hover:underline">
                        {row.phone}
                      </a>
                    ) : null}
                    {row.phone && row.email ? " · " : null}
                    {row.email ?? null}
                  </span>
                </td>

                <td>
                  {row.unitId ? (
                    <Link to={`/app/units/${row.unitId}`} className="link">
                      {row.unitLabel}
                    </Link>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                  {row.propertyName ? <span className="block text-xs text-ink-500">{row.propertyName}</span> : null}
                </td>

                {/* On the row, not behind a click: it is two lines, and it is what decides
                    whether this is a viewing or a wrong number. */}
                <td className="max-w-[26rem] text-ink-700">{row.message ?? <span className="text-ink-400">—</span>}</td>

                <td className="text-ink-500">{fmt.dateTime(row.createdAt)}</td>

                <td>
                  <Badge tone={TONE[row.status] ?? "muted"}>
                    {statuses.find((entry) => entry.value === row.status)?.label ?? row.status}
                  </Badge>
                </td>

                <td className="num">
                  <span className="inline-flex items-center gap-2">
                    {row.phone ? (
                      <a
                        href={whatsappHref(row.phone, `Hello ${row.name.split(" ")[0]},`)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-secondary btn-sm"
                      >
                        WhatsApp
                      </a>
                    ) : null}

                    {can("property.edit") ? (
                      <Select
                        aria-label={`Status of ${row.name}'s enquiry`}
                        value={row.status}
                        disabled={move.isPending}
                        onChange={(event) => move.mutate({ id: row.id, next: event.target.value })}
                        className="w-40"
                      >
                        {statuses.map((entry) => (
                          <option key={entry.value} value={entry.value}>
                            {entry.label}
                          </option>
                        ))}
                      </Select>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        )}

        {data?.meta ? <Pagination meta={data.meta} params={list} noun="enquiries" /> : null}
      </Card>
    </div>
  );
}
